/**
 * SMS Message Wording Configuration Service
 * -----------------------------------------
 * Stores the editable wording used to build booking SMS messages so that it is
 * shared by every user instead of being hardcoded in the front end.
 *
 * STORAGE NOTE
 * There is currently no dedicated settings endpoint on the API, so this service
 * persists the wording as a single reserved record via the existing
 * /comms/sms/templates endpoint. The record's answers[] array is used as a
 * key/value store, which keeps the data readable and avoids serialising JSON
 * into a text field.
 *
 * If a proper endpoint (e.g. /comms/sms/message-config) is added later, only
 * loadConfig() and saveConfig() need to change. Nothing that consumes this
 * service needs to be touched.
 */

import smsApiService from './smsApiService';

/** Reserved template name. Do not reuse this for a real conversation flow. */
export const CONFIG_RECORD_NAME = 'SYSTEM CONFIG - SMS Message Wording (do not delete)';

const CONFIG_RECORD_DESCRIPTION =
  'Reserved record holding editable SMS wording for the Booking screen. '
  + 'Managed via SMS Workspace > Message Text. Not a sendable conversation flow.';

/** Reply keywords are fixed: the backend matches inbound replies against them. */
export const REPLY_KEYWORDS = ['Yes', 'No', 'Call', 'Text'];

/** Appointment types that have their own wording. Mirrors APPOINTMENT_TYPES. */
export const CONFIGURABLE_TYPE_IDS = ['WHA', 'EPC', 'POST_EPC', 'POST_QI'];

/**
 * Placeholders an admin may insert. Shown as buttons on the admin screen.
 * `sample` drives the preview so admins see realistic output rather than tokens.
 */
export const AVAILABLE_PLACEHOLDERS = [
  { token: '{{TITLE}}', label: 'Contact title', sample: 'Mr' },
  { token: '{{LAST_NAME}}', label: 'Contact surname', sample: 'Okafor' },
  { token: '{{STREET_H}}', label: 'Contact street', sample: '14 Alder Road' },
  { token: '{{COMPANY}}', label: 'Company name', sample: 'Happy Energy Solutions' },
  { token: '{{SCHEME_NAME}}', label: 'Scheme name', sample: 'your landlord, Metropolitan Thames Valley Housing (MTVH)' },
  { token: '{{SCHEME_NUMBER}}', label: 'Scheme phone number', sample: '0115 784 3060' },
  { token: '{{ASSESSOR}}', label: 'Assessor name', sample: 'Daniel Cotter' },
  { token: '{{DATE}}', label: 'Appointment date', sample: 'Tuesday 2 September 2026' },
  { token: '{{TIME_TEXT}}', label: 'Slot phrase', sample: 'on the morning of' },
  { token: '{{TIME}}', label: 'Slot label', sample: 'AM' },
  { token: '{{TIME_DETAIL}}', label: 'Slot time range', sample: 'between 8.30am and 1pm' },
  { token: '{{OFFICE_HOURS}}', label: 'Office hours', sample: 'between 9am-5pm Monday to Friday' }
];

/**
 * Built-in defaults. These reproduce the wording that was previously hardcoded
 * in KarmaTextBooking.jsx, so behaviour is unchanged until an admin edits it.
 *
 * Two deliberate corrections against the original:
 *  - the Call response used a literal '0115 784 3060'; it now uses
 *    {{SCHEME_NUMBER}} so the Essex CC scheme quotes the correct number.
 *  - office hours were repeated in six places; they now come from
 *    {{OFFICE_HOURS}} so they are changed once.
 */
export const DEFAULT_CONFIG = {
  version: 1,
  updatedAt: null,
  updatedBy: null,
  shared: {
    officeHours: 'between 9am-5pm Monday to Friday',
    replyIntro: 'Please reply to this text with either',
    replyYes: 'if you are happy with the appointment time above',
    replyNo: 'if this is not a convenient time and we will suggest a different option',
    replyCall: 'if you want us to call to discuss',
    replyText: 'If you would like to text with a real person from our team',
    replyClosing: 'Or please feel free to call us on {{SCHEME_NUMBER}} {{OFFICE_HOURS}}.',
    offerBody: 'We can also offer you an appointment {{TIME_TEXT}} {{DATE}}, which would be {{TIME_DETAIL}}, is that convenient?',
    yesResponse: 'Thanks for confirming the appointment, please make sure to add it to your calendar.',
    callResponse: 'Thanks for requesting a call, we will contact you on this number to discuss when might be convenient, if you want us to call a different number please text that to us now, or please feel free to call us on {{SCHEME_NUMBER}} {{OFFICE_HOURS}}.',
    textResponse: 'Ok, thanks for confirming you wish to text with one of our team members, someone will respond shortly, but in the meantime, please feel free to send us a further message, such as any preferred dates, or relevant information, to allow our team member to be ready to respond',
    noFinalResponse: 'Ok thanks for confirming that the appointment was not convenient, we will call you to discuss when might be convenient, or please feel free to call us on {{SCHEME_NUMBER}} {{OFFICE_HOURS}}.',
    invalidResponse: 'Apologies but this texting service can only respond to Yes, No, Call, or Text. Please respond with one of these 4 options, or please feel free to call us on {{SCHEME_NUMBER}} {{OFFICE_HOURS}} or email us at info@happyenergy.co.uk. A team member will also check your message and respond if we do not hear from you.'
  },
  types: {
    WHA: {
      body: 'Dear {{TITLE}} {{LAST_NAME}}, this is {{COMPANY}}, we are contacting you on behalf of {{SCHEME_NAME}} to arrange the booking of a Whole House Assessment (WHA) for {{STREET_H}}. The assessment will take around 1-2 hours and we would like to book this {{TIME_TEXT}} {{DATE}} with our assessor {{ASSESSOR}}. This is an {{TIME}} appointment, which means our assessor will arrive {{TIME_DETAIL}}, but they will call you the night before to give you a more accurate time.'
    },
    EPC: {
      body: 'Dear {{TITLE}} {{LAST_NAME}}, this is {{COMPANY}}, we are contacting you on behalf of {{SCHEME_NAME}} to arrange the booking of an Energy Performance Certificate (EPC) for {{STREET_H}}. The assessment will take around 1 hour and we would like to book this {{TIME_TEXT}} {{DATE}} with our assessor {{ASSESSOR}}. This is an {{TIME}} appointment, which means our assessor will arrive {{TIME_DETAIL}}, but they will call you the night before to give you a more accurate time.'
    },
    POST_EPC: {
      body: 'Dear {{TITLE}} {{LAST_NAME}}, this is {{COMPANY}}, following the completion of your energy saving works, we are contacting you on behalf of {{SCHEME_NAME}} to arrange the booking of an Energy Performance Certificate (EPC) for {{STREET_H}}. The assessment will take around 30 minutes and we would like to book this {{TIME_TEXT}} {{DATE}} with our assessor {{ASSESSOR}}. This is an {{TIME}} appointment, which means our assessor will arrive {{TIME_DETAIL}}, but they will call you the night before to give you a more accurate time.'
    },
    POST_QI: {
      body: 'Dear {{TITLE}} {{LAST_NAME}}, this is {{COMPANY}}, following the completion of your energy saving works, we are contacting you on behalf of {{SCHEME_NAME}} to arrange the booking of a quality inspection for {{STREET_H}}. The assessment will take around 30 minutes and we would like to book this {{TIME_TEXT}} {{DATE}} with our assessor {{ASSESSOR}}. This is an {{TIME}} appointment, which means our assessor will arrive {{TIME_DETAIL}}, but they will call you the night before to give you a more accurate time.'
    }
  }
};

const clone = (value) => JSON.parse(JSON.stringify(value));

/** Merge a loaded config over the defaults so a partial record never breaks sending. */
export const withDefaults = (loaded) => {
  const base = clone(DEFAULT_CONFIG);
  if (!loaded || typeof loaded !== 'object') return base;

  base.version = loaded.version || base.version;
  base.updatedAt = loaded.updatedAt || null;
  base.updatedBy = loaded.updatedBy || null;
  base.shared = { ...base.shared, ...(loaded.shared || {}) };

  CONFIGURABLE_TYPE_IDS.forEach((typeId) => {
    base.types[typeId] = {
      ...base.types[typeId],
      ...((loaded.types || {})[typeId] || {})
    };
  });

  return base;
};

/* ------------------------------------------------------------------ *
 * Encoding to and from the templates endpoint                         *
 * ------------------------------------------------------------------ */

const encodeToRecord = (config) => ({
  name: CONFIG_RECORD_NAME,
  description: CONFIG_RECORD_DESCRIPTION,
  stages: [
    {
      question: 'SHARED',
      isInitial: true,
      answers: Object.entries(config.shared).map(([key, value]) => ({
        text: key,
        response: String(value ?? ''),
        nextStageNumber: null
      })),
      defaultResponse: null
    },
    ...CONFIGURABLE_TYPE_IDS.map((typeId) => ({
      question: typeId,
      isInitial: false,
      answers: Object.entries(config.types[typeId] || {}).map(([key, value]) => ({
        text: key,
        response: String(value ?? ''),
        nextStageNumber: null
      })),
      defaultResponse: null
    })),
    {
      question: 'META',
      isInitial: false,
      answers: [
        { text: 'version', response: String(config.version || 1), nextStageNumber: null },
        { text: 'updatedAt', response: String(config.updatedAt || ''), nextStageNumber: null },
        { text: 'updatedBy', response: String(config.updatedBy || ''), nextStageNumber: null }
      ],
      defaultResponse: null
    }
  ]
});

const answersToObject = (answers) => {
  const output = {};
  (Array.isArray(answers) ? answers : []).forEach((answer) => {
    // The DB round-trip returns Sequelize column names (AnswerText/ResponseMessage),
    // not the text/response shape used when saving; without this, every decode was empty.
    const key = answer?.text ?? answer?.Text ?? answer?.AnswerText;
    if (!key) return;
    output[key] = answer?.response ?? answer?.Response ?? answer?.ResponseMessage ?? '';
  });
  return output;
};

const decodeFromRecord = (record) => {
  const stages = record?.stages || record?.Stages || [];
  const decoded = { shared: {}, types: {} };

  stages.forEach((stage) => {
    const marker = String(stage?.question ?? stage?.Question ?? '').trim();
    const values = answersToObject(stage?.answers ?? stage?.Answers);

    if (marker === 'SHARED') {
      decoded.shared = values;
    } else if (marker === 'META') {
      decoded.version = Number.parseInt(values.version, 10) || 1;
      decoded.updatedAt = values.updatedAt || null;
      decoded.updatedBy = values.updatedBy || null;
    } else if (CONFIGURABLE_TYPE_IDS.includes(marker)) {
      decoded.types[marker] = values;
    }
  });

  return decoded;
};

const findConfigRecord = async (instance) => {
  const response = await smsApiService.getTemplates(instance);
  const templates = Array.isArray(response?.data) ? response.data : [];
  return templates.find(
    (template) => String(template?.Name || template?.name || '').trim() === CONFIG_RECORD_NAME
  ) || null;
};

const recordId = (record) => {
  const raw = record?.Id ?? record?.id;
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/* ------------------------------------------------------------------ *
 * Public API                                                          *
 * ------------------------------------------------------------------ */

/**
 * Load the shared wording.
 * Never throws: on any failure it returns the built-in defaults so that the
 * booking screen continues to work exactly as it did before.
 *
 * @returns {Promise<{config: object, isStored: boolean, error: string|null}>}
 */
export const loadConfig = async (instance) => {
  try {
    const record = await findConfigRecord(instance);
    if (!record) {
      return { config: withDefaults(null), isStored: false, error: null };
    }

    const id = recordId(record);
    // The list endpoint may return a summary, so fetch the full record.
    const full = id ? (await smsApiService.getTemplate(id, instance))?.data : record;
    return { config: withDefaults(decodeFromRecord(full)), isStored: true, error: null };
  } catch (error) {
    console.error('[smsMessageConfig] Falling back to built-in wording:', error);
    return {
      config: withDefaults(null),
      isStored: false,
      error: error?.response?.data?.error || error?.message || 'Could not load saved wording.'
    };
  }
};

/**
 * Save the shared wording. Creates the record on first save, updates it after.
 * @returns {Promise<{ok: boolean, error: string|null}>}
 */
export const saveConfig = async (config, instance, updatedBy) => {
  try {
    const payload = encodeToRecord({
      ...withDefaults(config),
      version: (config?.version || 1),
      updatedAt: new Date().toISOString(),
      updatedBy: updatedBy || 'unknown'
    });

    const existing = await findConfigRecord(instance);
    const id = recordId(existing);

    if (id) {
      await smsApiService.updateTemplate(id, payload, instance);
    } else {
      await smsApiService.saveTemplate(payload, instance);
    }

    return { ok: true, error: null };
  } catch (error) {
    console.error('[smsMessageConfig] Save failed:', error);
    return {
      ok: false,
      error: error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || 'Could not save. Your changes have not been stored.'
    };
  }
};

/* ------------------------------------------------------------------ *
 * Validation                                                          *
 * ------------------------------------------------------------------ */

/**
 * Find any {{PLACEHOLDER}} left in a message after substitution.
 * Anything still present means the underlying data was missing, or an admin
 * typed a token the system does not recognise. Either way it must not be sent
 * to a customer as literal text.
 *
 * @returns {string[]} unique unresolved tokens, e.g. ['{{STREET_H}}']
 */
export const findUnresolvedPlaceholders = (text) => {
  const found = String(text || '').match(/\{\{[A-Za-z0-9_]+\}\}/g) || [];
  return [...new Set(found)];
};

/**
 * Check a set of named messages for unresolved placeholders.
 * @param {Object<string,string>} messages label -> rendered message text
 * @returns {{ok: boolean, problems: Array<{label: string, tokens: string[]}>, tokens: string[]}}
 */
export const validateMessages = (messages) => {
  const problems = [];
  const all = new Set();

  Object.entries(messages || {}).forEach(([label, text]) => {
    if (!String(text || '').trim()) return;
    const tokens = findUnresolvedPlaceholders(text);
    if (tokens.length) {
      problems.push({ label, tokens });
      tokens.forEach((token) => all.add(token));
    }
  });

  return { ok: problems.length === 0, problems, tokens: [...all] };
};

/** Human-readable explanation of why a token is unresolved. */
export const PLACEHOLDER_HINTS = {
  '{{STREET_H}}': 'the selected Karma contact has no street address',
  '{{DATE}}': 'no appointment date has been entered',
  '{{TIME_TEXT}}': 'no appointment date has been entered',
  '{{TIME}}': 'no appointment time has been selected',
  '{{TIME_DETAIL}}': 'no appointment time has been selected',
  '{{TITLE}}': 'the contact has no title',
  '{{LAST_NAME}}': 'the contact has no surname',
  '{{ASSESSOR}}': 'no assessor has been selected',
  '{{SCHEME_NAME}}': 'the selected scheme has no scheme name set',
  '{{SCHEME_NUMBER}}': 'the selected scheme has no phone number set',
  '{{COMPANY}}': 'the selected scheme has no company name set',
  '{{OFFICE_HOURS}}': 'office hours are not set in Message Text'
};

/* ------------------------------------------------------------------ *
 * Message assembly                                                    *
 * ------------------------------------------------------------------ */

/** Build the fixed reply block. Keywords are not editable by design. */
export const buildReplyBlock = (shared) => {
  const s = { ...DEFAULT_CONFIG.shared, ...(shared || {}) };
  return [
    s.replyIntro,
    '',
    `Yes - ${s.replyYes}`,
    '',
    `No - ${s.replyNo}`,
    '',
    `Call - ${s.replyCall}`,
    '',
    `Text - ${s.replyText}`,
    '',
    s.replyClosing
  ].join('\n');
};

/**
 * Assemble the complete outbound message for an appointment type.
 * Returns text still containing {{PLACEHOLDERS}} — substitution happens
 * downstream so the same string can be previewed or sent.
 */
export const buildOutboundTemplate = (config, appointmentTypeId) => {
  const merged = withDefaults(config);
  const body = merged.types[appointmentTypeId]?.body || merged.types.WHA.body;
  return `${body}\n\n${buildReplyBlock(merged.shared)}`;
};

/** Assemble an alternative-appointment offer message. */
export const buildOfferTemplate = (config) => {
  const merged = withDefaults(config);
  return `${merged.shared.offerBody}\n\n${buildReplyBlock(merged.shared)}`;
};

const messageConfigService = {
  loadConfig,
  findUnresolvedPlaceholders,
  validateMessages,
  PLACEHOLDER_HINTS,
  saveConfig,
  withDefaults,
  buildOutboundTemplate,
  buildOfferTemplate,
  buildReplyBlock,
  DEFAULT_CONFIG,
  AVAILABLE_PLACEHOLDERS,
  CONFIGURABLE_TYPE_IDS,
  REPLY_KEYWORDS,
  CONFIG_RECORD_NAME
};

export default messageConfigService;
